import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

// Public
import HomePage from "@/pages/public/HomePage";
import PlatformPage from "@/pages/public/PlatformPage";
import ModulePage from "@/pages/public/ModulePage";
import PricingPage from "@/pages/public/PricingPage";
import PartnersPage from "@/pages/public/PartnersPage";
import CompliancePage from "@/pages/public/CompliancePage";
import CompanyPage from "@/pages/public/CompanyPage";
import AuditPage from "@/pages/public/AuditPage";
import StandardPage from "@/pages/public/StandardPage";
import AlignmentApplyPage from "@/pages/public/AlignmentApplyPage";
import StandardVerifiedPage from "@/pages/public/StandardVerifiedPage";
import { PrivacyPage, TermsPage, DsarPage } from "@/pages/public/LegalPages";
import { CheckoutSuccess, CheckoutCancel } from "@/pages/public/CheckoutPages";
import NotFoundPage from "@/pages/public/NotFoundPage";
import SizzlePage from "@/pages/public/SizzlePage";
import SizzlePlayerPage from "@/pages/public/SizzlePlayerPage";

// Auth
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/auth/AuthPages";

// Portals
import PortalShell from "@/pages/PortalShell";
import { ClientDashboard, ClientNewOrder, ClientDeliverables, ClientAudits, ClientAccount } from "@/pages/client/ClientPages";
import { ClientBilling, ClientUniverseCounts, ClientPixel } from "@/pages/client/ClientPhase2";
import { PartnerDashboard, PartnerNewOrder, PartnerBrand, PartnerWholesale } from "@/pages/partner/PartnerPages";
import { BuyerDashboard, BuyerStandingOrders, BuyerLeads, BuyerLedger, BuyerProfile } from "@/pages/buyer/BuyerPages";
import { AdminMetrics, AdminPipeline, AdminFulfillment, AdminAuditLog, AdminOutbox, AdminAdapterQueue, AdminCRM } from "@/pages/admin/AdminPages";
import { AdminConfigStudio } from "@/pages/admin/AdminConfigStudio";
import AdminLeadExchange from "@/pages/admin/AdminLeadExchange";
import { AdminIntegrations, AdminAdjudication, AdminReports } from "@/pages/admin/AdminPhase2";
import { AdminAgentStudio } from "@/pages/admin/AdminAgentStudio";
import AdminAlignmentBoard from "@/pages/admin/AdminAlignmentBoard";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/sizzle" element={<SizzlePlayerPage />} />
            <Route path="/_sizzle/:act" element={<SizzlePage />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/modules/:code" element={<ModulePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/company" element={<CompanyPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/standard" element={<StandardPage />} />
            <Route path="/standard-verified/:slug" element={<StandardVerifiedPage />} />
            <Route path="/alignment/apply" element={<AlignmentApplyPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/dsar" element={<DsarPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Client Portal */}
            <Route path="/client" element={<ProtectedRoute role={["client","admin"]}><PortalShell role="client" /></ProtectedRoute>}>
              <Route index element={<ClientDashboard />} />
              <Route path="orders/new" element={<ClientNewOrder role="client" />} />
              <Route path="universe" element={<ClientUniverseCounts />} />
              <Route path="deliverables" element={<ClientDeliverables />} />
              <Route path="pixel" element={<ClientPixel />} />
              <Route path="audits" element={<ClientAudits />} />
              <Route path="billing" element={<ClientBilling />} />
              <Route path="account" element={<ClientAccount />} />
            </Route>

            {/* Partner Portal */}
            <Route path="/partner" element={<ProtectedRoute role={["partner","admin"]}><PortalShell role="partner" /></ProtectedRoute>}>
              <Route index element={<PartnerDashboard />} />
              <Route path="orders/new" element={<PartnerNewOrder role="partner" />} />
              <Route path="brand" element={<PartnerBrand />} />
              <Route path="wholesale" element={<PartnerWholesale />} />
            </Route>

            {/* Buyer Portal */}
            <Route path="/buyer" element={<ProtectedRoute role={["buyer","admin"]}><PortalShell role="buyer" /></ProtectedRoute>}>
              <Route index element={<BuyerDashboard />} />
              <Route path="standing-orders" element={<BuyerStandingOrders />} />
              <Route path="leads" element={<BuyerLeads />} />
              <Route path="ledger" element={<BuyerLedger />} />
              <Route path="profile" element={<BuyerProfile />} />
            </Route>

            {/* Admin Command Center */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><PortalShell role="admin" /></ProtectedRoute>}>
              <Route index element={<AdminMetrics />} />
              <Route path="agent" element={<AdminAgentStudio />} />
              <Route path="alignment" element={<AdminAlignmentBoard />} />
              <Route path="pipeline" element={<AdminPipeline />} />
              <Route path="fulfillment" element={<AdminFulfillment />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="lead-exchange" element={<AdminLeadExchange />} />
              <Route path="adjudication" element={<AdminAdjudication />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="crm" element={<AdminCRM />} />
              <Route path="config" element={<AdminConfigStudio />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="outbox" element={<AdminOutbox />} />
              <Route path="adapter-queue" element={<AdminAdapterQueue />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
